<div class="passport-page" [class.is-confirmed]="isConfirmed">

  <!-- ===================== Passport hero ===================== -->
  <section class="passport-hero" role="region" [attr.aria-label]="patient.given_name + ' ' + patient.family_name">
    <div class="hero-top">
      <div class="avatar" aria-hidden="true">{{ initials }}</div>
      <div class="identity">
        <h2 class="name">{{ patient.given_name }} {{ patient.family_name }}</h2>
        <p class="sub">
          @if (age !== null) {
            @if (age >= 1) {
              <span>{{ 'PATIENT_DETAIL.HOME.AGE_YEARS' | translate:{ age: age } }}</span>
            } @else if (ageMonths >= 1) {
              <span>{{ 'PATIENT_DETAIL.HOME.AGE_MONTHS' | translate:{ months: ageMonths } }}</span>
            } @else {
              <span>{{ 'PATIENT_DETAIL.HOME.AGE_NEWBORN' | translate }}</span>
            }
          }
          @if (age !== null && getCountryName()) {
            <span aria-hidden="true"> · </span>
          }
          @if (getCountryName()) {
            <span>{{ getCountryName() }}</span>
          }
        </p>
      </div>
      @if (isConfirmed) {
        <mat-icon class="verified" [attr.aria-label]="statusKey | translate">verified</mat-icon>
      }
    </div>

    <div class="hero-meta">
      <div class="meta-block">
        <span class="meta-key">{{ 'PATIENT_DETAIL.CONTENT.RETTXID.TITLE' | translate }}</span>
        <button
          type="button"
          class="rettxid"
          (click)="copyRettxid()"
          [matTooltip]="'PATIENT_DETAIL.CONTENT.RETTXID.COPY_TOOLTIP' | translate"
          [attr.aria-label]="'PATIENT_DETAIL.CONTENT.RETTXID.COPY_BUTTON' | translate">
          <span class="rettxid-value">{{ patient.rettxid }}</span>
          <mat-icon aria-hidden="true">content_copy</mat-icon>
        </button>
      </div>
    </div>
  </section>

  <!-- ===================== Confirmed banner ===================== -->
  @if (isConfirmed) {
    <section class="confirmed-banner" role="status">
      <mat-icon aria-hidden="true">check_circle</mat-icon>
      <div class="cb-text">
        <div class="cb-title">{{ 'PATIENT_DETAIL.HOME.BANNER_DONE_TITLE' | translate }}</div>
        <div class="cb-sub">{{ 'PATIENT_DETAIL.HOME.BANNER_DONE_SUBTITLE' | translate }}</div>
      </div>
    </section>
  }

  <!-- ===================== Quick facts ===================== -->
  <section class="quick-facts" role="list">
    <div class="qf-cell" role="listitem">
      <span class="qf-key">{{ 'PATIENT_DETAIL.CONTENT.INFORMATION.FORM.DATE_OF_BIRTH' | translate }}</span>
      <span class="qf-val">{{ patient.date_of_birth ? (patient.date_of_birth | date:'longDate':undefined:currentLang) : '—' }}</span>
    </div>
    <div class="qf-cell" role="listitem">
      <span class="qf-key">{{ 'PATIENT_DETAIL.CONTENT.INFORMATION.FORM.GENDER' | translate }}</span>
      <span class="qf-val">{{ getGenderName() || '—' }}</span>
    </div>
    @if (!isConfirmed && hasMutation) {
      <div class="qf-cell mutation" role="listitem">
        <span class="qf-key">{{ 'PATIENT_DETAIL.HOME.MUTATION' | translate }}</span>
        <span class="qf-val">{{ mutationCoding }}@if (mutationProtein) {<span class="protein"> · {{ mutationProtein }}</span>}@if (isMultiMutation) {<span class="qf-more"> +{{ mutationCount - 1 }}</span>}</span>
      </div>
    }
  </section>

  <!-- ===================== Genetic report / Confirmed mutation ===================== -->
  @if (isConfirmed) {
    <section class="mutation-card" role="region"
             [attr.aria-label]="'PATIENT_DETAIL.HOME.MUTATION_DETAILS_TITLE' | translate">
      <button
        type="button"
        class="mc-head"
        (click)="toggleMutationDetails()"
        [attr.aria-expanded]="mutationDetailsOpen"
        aria-controls="mutation-details-body">
        <span class="mc-dot" aria-hidden="true"><mat-icon>biotech</mat-icon></span>
        <span class="mc-ttl">{{ mutationDetailsTitleKey | translate }}</span>
        @if (isMultiMutation) {
          <span class="mc-count" aria-hidden="true">{{ mutationCount }}</span>
        }
        <mat-icon class="mc-chev" [class.open]="mutationDetailsOpen" aria-hidden="true">expand_more</mat-icon>
      </button>
      @if (mutationDetailsOpen) {
        <div id="mutation-details-body">
          @if (mutationViews.length) {
            @for (mv of mutationViews; track mv.index) {
              <div class="mc-group">
                @if (isMultiMutation) {
                  <h3 class="mc-group-ttl">
                    {{ mv.coding }}
                    @if (mv.protein) {<span class="mc-group-protein"> · {{ mv.protein }}</span>}
                  </h3>
                }
                <dl class="mc-grid">
                  @if (!isMultiMutation) {
                    <div class="mc-row">
                      <dt>{{ 'PATIENT_DETAIL.HOME.MUTATION_FIELD_CODING' | translate }}</dt>
                      <dd>{{ mv.coding }}</dd>
                    </div>
                  }
                  @if (mv.protein) {
                    <div class="mc-row">
                      <dt>{{ 'PATIENT_DETAIL.HOME.MUTATION_FIELD_PROTEIN' | translate }}</dt>
                      <dd>{{ mv.protein }}</dd>
                    </div>
                  }
                  @if (mv.genomic) {
                    <div class="mc-row">
                      <dt>{{ 'PATIENT_DETAIL.HOME.MUTATION_FIELD_GENOMIC' | translate }}</dt>
                      <dd>{{ mv.genomic }}</dd>
                    </div>
                  }
                  @if (mv.variantType) {
                    <div class="mc-row">
                      <dt>{{ 'PATIENT_DETAIL.HOME.MUTATION_FIELD_TYPE' | translate }}</dt>
                      <dd>{{ mv.variantType }}</dd>
                    </div>
                  }
                  @if (mv.transcript) {
                    <div class="mc-row">
                      <dt>{{ 'PATIENT_DETAIL.HOME.MUTATION_FIELD_TRANSCRIPT' | translate }}</dt>
                      <dd>{{ mv.transcript }}</dd>
                    </div>
                  }
                </dl>
              </div>
            }
          } @else {
            <p class="mc-empty">{{ 'PATIENT_DETAIL.HOME.MUTATION_DETAILS_EMPTY' | translate }}</p>
          }
        </div>
      }
    </section>
  } @else {
    <section class="genetics-card" [class.actionable]="needsGeneticsUpload">
      @if (showProgress) {
        <div class="genetics-progress" [attr.data-variant]="progressVariant">
          <div class="gp-row">
            <span class="gp-label">{{ 'PATIENT_DETAIL.HOME.PROGRESS_LABEL' | translate }}</span>
            <span class="gp-pct" aria-hidden="true">{{ progressPct }}%</span>
          </div>
          <div class="gp-track"
               role="progressbar"
               [attr.aria-valuenow]="progressPct" aria-valuemin="0" aria-valuemax="100">
            <div class="gp-fill" [style.width.%]="progressPct"></div>
          </div>
          <p class="gp-hint">{{ progressHintKey | translate }}</p>
        </div>
      }
      <app-diagnosis-tab
        [patient]="patient"
        (fileStateChange)="needsGeneticsUpload = $event"
        (patientRefreshNeeded)="refreshPatientData()">
      </app-diagnosis-tab>
    </section>
  }

  <!-- ===================== Personal information (collapsible) ===================== -->
  <section class="info-section">
    <button
      type="button"
      class="section-head"
      (click)="togglePersonalInfo()"
      [attr.aria-expanded]="personalInfoOpen"
      aria-controls="personal-info-body">
      <mat-icon class="lead" aria-hidden="true">badge</mat-icon>
      <span class="section-title">{{ 'PATIENT_DETAIL.CONTENT.INFORMATION.TITLE' | translate }}</span>
      <mat-icon class="chev" [class.open]="personalInfoOpen" aria-hidden="true">expand_more</mat-icon>
    </button>

    @if (personalInfoOpen) {
      <div class="section-body" id="personal-info-body">
        <form [formGroup]="form" class="patient-form">

          <mat-form-field appearance="outline" class="w-100">
            <mat-label>{{ 'PATIENT_DETAIL.CONTENT.INFORMATION.FORM.NAME' | translate }}</mat-label>
            <input title="name" matInput type="text" formControlName="given_name" trimOnBlur
                   [placeholder]="'PATIENT_DETAIL.CONTENT.INFORMATION.FORM.NAME_PLACEHOLDER' | translate" />
            @if (form.get('given_name')?.invalid && form.get('given_name')?.touched) {
              <mat-error>{{ 'PATIENT_DETAIL.VALIDATION.NAME.REQUIRED' | translate }}</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-100">
            <mat-label>{{ 'PATIENT_DETAIL.CONTENT.INFORMATION.FORM.LAST_NAME' | translate }}</mat-label>
            <input title="surname" matInput type="text" formControlName="family_name" trimOnBlur
                   [placeholder]="'PATIENT_DETAIL.CONTENT.INFORMATION.FORM.LAST_NAME_PLACEHOLDER' | translate" />
            @if (form.get('family_name')?.invalid && form.get('family_name')?.touched) {
              <mat-error>{{ 'PATIENT_DETAIL.VALIDATION.LAST_NAME.REQUIRED' | translate }}</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-100">
            <mat-label>{{ 'PATIENT_DETAIL.CONTENT.INFORMATION.FORM.GENDER' | translate }}</mat-label>
            <mat-select formControlName="gender">
              @for (item of genders; track item.id) {
                <mat-option [value]="item.id">{{ item.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-100">
            <mat-label>{{ 'PATIENT_DETAIL.CONTENT.INFORMATION.FORM.DATE_OF_BIRTH' | translate }}</mat-label>
            <input title="birthdate" matInput type="text" formControlName="date_of_birth"
                   [max]="maxDate" [min]="minDate"
                   [placeholder]="'MODAL_ADD_PATIENT.FORM.DATE_OF_BIRTH_PLACEHOLDER' | translate"
                   [matDatepicker]="picker" readonly>
            <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
            @if (form.get('date_of_birth')?.invalid && form.get('date_of_birth')?.touched) {
              <mat-error>{{ 'PATIENT_DETAIL.VALIDATION.BIRTH_DATE.REQUIRED' | translate }}</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-100">
            <mat-label>{{ 'MODAL_ADD_PATIENT.FORM.COUNTRY_OF_BIRTH' | translate }}</mat-label>
            <mat-select formControlName="country_of_birth">
              @for (item of countries; track item.iso_code) {
                <mat-option [value]="item.iso_code">{{ item.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-100">
            <mat-label>{{ 'PATIENT_DETAIL.CONTENT.INFORMATION.FORM.RELATIONSHIP' | translate }}</mat-label>
            <mat-select formControlName="relationship">
              @for (item of relationships; track item.id) {
                <mat-option [value]="item.id">{{ item.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <!-- Save / cancel while editing -->
          @if (isEditing) {
            <div class="form-actions editing">
              <button type="button" class="btn btn-cancel" (click)="disableEditing()">
                <mat-icon aria-hidden="true">close</mat-icon>
                {{ 'PATIENT_DETAIL.ACTIONS.CANCEL' | translate }}
              </button>
              <button type="button" class="btn btn-save" (click)="saveInfoPatient()" [disabled]="!form.valid">
                <mat-icon aria-hidden="true">check</mat-icon>
                {{ 'PATIENT_DETAIL.ACTIONS.SAVE' | translate }}
              </button>
            </div>
          }
        </form>

        @if (!isEditing && (canEditPatient || canRequestCorrectionPatient)) {
          <div class="section-actions">
            @if (canEditPatient) {
              <div class="edit-active">
                <button type="button" class="btn btn-edit" (click)="onEditClick()"
                        [attr.aria-label]="'PATIENT_DETAIL.ACTIONS.EDIT' | translate">
                  <mat-icon aria-hidden="true">edit</mat-icon>
                  {{ 'PATIENT_DETAIL.ACTIONS.EDIT' | translate }}
                </button>

                @if (canDeletePatient) {
                  <button type="button" class="btn btn-icon btn-delete" (click)="onDeleteClick()"
                          [matTooltip]="'PATIENT_DETAIL.ACTIONS.DELETE' | translate"
                          [attr.aria-label]="'PATIENT_DETAIL.ACTIONS.DELETE' | translate">
                    <mat-icon aria-hidden="true">delete_outline</mat-icon>
                  </button>
                }
              </div>
            } @else {
              <!-- Confirmed / locked: profile is frozen, correction is the path -->
              <div class="locked-card">
                <mat-icon aria-hidden="true">lock</mat-icon>
                <div class="lk-text">
                  <div class="lk-title">{{ 'PATIENT_DETAIL.HOME.LOCKED_TITLE' | translate }}</div>
                  <div class="lk-sub">{{ 'PATIENT_DETAIL.HOME.LOCKED_SUBTITLE' | translate }}</div>
                </div>
              </div>
              <button type="button" class="btn btn-correction" (click)="openCorrectionInfo()"
                      [attr.aria-label]="'PATIENT_DETAIL.ACTIONS.REQUEST_CORRECTION' | translate">
                <mat-icon aria-hidden="true">edit_note</mat-icon>
                {{ 'PATIENT_DETAIL.ACTIONS.REQUEST_CORRECTION' | translate }}
              </button>
            }
          </div>
        }
      </div>
    }
  </section>

</div>
